import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import {Tag} from "primereact/tag";
import {I18n} from "react-redux-i18n";
import {TabPanel, TabView} from "primereact/tabview";
import {Toolbar} from "primereact/toolbar";
import {Button} from "primereact/button";
import {Chart} from "primereact/chart";
import {Dropdown} from "primereact/dropdown";
import {useSelector} from "react-redux";
import {formatAmount, formatCustomDate, getApiKey} from "../helpers/utils";
import StatsService from "../services/StatsService";
import {Dialog} from 'primereact/dialog';
import {InputNumber} from 'primereact/inputnumber';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import ApiService from "../services/ApiService";
import {Toast} from 'primereact/toast';
import MovementService from "../services/MovementService";
import {Order} from "../helpers/order";

export default function Balance() {

    const statService = useRef(new StatsService());
    const apiService = useRef(new ApiService());
    const movementService = useRef(new MovementService());
    const business = useSelector(state => state.business);

    const sourceCurrencies = useSelector(
        state => state.settings?.SOURCE_CURRENCIES
    );

    const targetCurrencies = useSelector(
        state => state.settings?.TARGET_CURRENCIES
    );

    const locale = useSelector(
        state => state.i18n?.locale
    );

    const toast = useRef(null);
    const dt = useRef(null);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [pageSize,] = useState(5);
    const [totalRecords, setTotalRecords] = useState(0);
    const [elements, setElements] = useState([]);
    const [sortFilterValue, setSortFilterValue] = useState(null);
    const [sortFilterOrder, setSortFilterOrder] = useState(1);

    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [doughnutData, setDoughnutData] = useState();
    const [totalPrice, setTotalPrice] = useState();
    const [doughnutOptions, setDoughnutOptions] = useState();
    const [balanceMode, setBalanceMode] = useState("AVAILABLE_BALANCE");
    const [currencyCode, setCurrencyCode] = useState(business?.current?.currencyCode);
    const [prices, setPrices] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const doughnut = useRef(null);
    const balanceModeOptions = [
        {label: I18n.t('AVAILABLE_BALANCE'), value: 'AVAILABLE_BALANCE'},
        {label: I18n.t('FORECAST_BALANCE'), value: 'FORECAST_BALANCE'},
    ];

    const [showConversionDialog, setShowConversionDialog] = useState(false);
    const [toAmount, setToAmount] = useState(null);
    const [movementIdentifier, setMovementIdentifier] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = useCallback(({businessIdentifier, isLive, start, pageSize, sortField, order}) => {
        setLoading(true);
        let searchParams = [
            {field: 'business_identifier', value: businessIdentifier},
            {field: 'is_live', value: isLive}
        ];

        let sortOrder = order === -1 ? Order.ASC : Order.DESC;
        movementService.current.getPage(
            start,
            pageSize,
            sortField,
            sortOrder,
            searchParams
        ).then(response => {
            setElements(response.data.content.list || []);
            setTotalRecords(response.data.content.totalCount);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
        });
    }, [movementService]);

    const onPage = useCallback((event) => {
        const {page, first, rows} = event;
        loadData({
            businessIdentifier: business?.current?.identifier,
            isLive: business?.isBusinessProduction,
            start: page,
            pageSize: rows,
            sortField: sortFilterValue,
            order: sortFilterOrder,
        });
        setFirst(first);
    }, [business, sortFilterValue, sortFilterOrder, loadData]);


    useEffect(() => {
        setDoughnutOptions(getDoughnutOptions)
        loadData({
            businessIdentifier: business?.current?.identifier,
            isLive: business?.isBusinessProduction,
            start: 0,
            pageSize: pageSize,
            sortField: sortFilterValue,
            order: sortFilterOrder,
        });
    }, []);

    useEffect(() => {
        loadData({
            businessIdentifier: business?.current?.identifier,
            isLive: business?.isBusinessProduction,
            start: first,
            pageSize: pageSize,
            sortField: sortFilterValue,
            order: sortFilterOrder
        });
    }, [locale, business]);

    const customSort = useCallback((event) => {
        const {sortField, sortOrder} = event;
        setSortFilterValue(sortField);
        setSortFilterOrder(sortOrder);
        loadData({
            businessIdentifier: business?.current?.identifier,
            isLive: business?.isBusinessProduction,
            start: first,
            pageSize: pageSize,
            sortField: sortField,
            order: sortOrder,
        });
    }, [business, first, pageSize, loadData]);

    const handleBalanceModeChange = (e) => {
        setBalanceMode(e.value);
        console.log('Ordre de tri sélectionné :', e.value);
    };

    const handleCurrencyChange = (e) => {
        setCurrencyCode(e.value);
        console.log('La devise est :', e.value);
    };

    const validationSchema = Yup.object().shape({
        fromAmount: Yup.number()
            .required(I18n.t('FIELD_IS_REQUIRED'))
            .positive(I18n.t('AMOUNT_MUST_BE_POSITIVE'))
            .typeError(I18n.t('AMOUNT_MUST_BE_POSITIVE')),
        fromCurrencyCode: Yup.string()
            .required(I18n.t('FIELD_IS_REQUIRED')),
        toCurrencyCode: Yup.string()
            .required(I18n.t('FIELD_IS_REQUIRED')),
        fromProvider: Yup.string()
            .required(I18n.t('FIELD_IS_REQUIRED')),
        toProvider: Yup.string()
            .required(I18n.t('FIELD_IS_REQUIRED'))
    });

    // Initial values for the form
    const getInitialValues = () => ({
        fromAmount: null,
        fromCurrencyCode: '',
        fromProvider: '',
        toCurrencyCode: '',
        toProvider: ''
    });

    const openConversionDialog = () => {
        setToAmount(null);
        setShowConversionDialog(true);
    };

    const hideConversionDialog = () => {
        setShowConversionDialog(false);
    };

    const estimateConvert = (values) => {
        setIsLoading(true)
        apiService.current.post("/v1/movements/estimate-convertion", getApiKey(business, true), values)
            .then(response => {
                setToAmount(response.data.content.amount);
                setMovementIdentifier(response.data.content.id);
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error converting currency:', error);
                setIsLoading(false);
            });
    };

    const handleConvert = () => {
        setIsLoading(true)
        apiService.current.patch("/v1/movements/validate-conversion/" + movementIdentifier, getApiKey(business, true))
            .then(response => {
                setIsLoading(false);
                hideConversionDialog()
                toast.current.show({
                    severity: 'success',
                    summary: I18n.t('CONVERT_CURRENCY'),
                    detail: I18n.t('CONVERT_CURRENCY_SUCCESSFUL'),
                    life: 6000
                });
            })
            .catch(error => {
                console.error('Error converting currency:', error);
                setIsLoading(false);
                toast.current.show({
                    severity: 'error',
                    summary: I18n.t('CONVERT_CURRENCY'),
                    detail: I18n.t(error.response.data.code),
                    life: 6000
                });
            });
    };

    const sourceCurrenciesOptions = useMemo(() => {
        if (!sourceCurrencies || sourceCurrencies.length === 0) return [];
        return Object.entries(sourceCurrencies).map(([key, providers]) => ({
            value: key,
            providers,
            label: I18n.t(`${key}_DESCRIPTION`)
        }));
    }, [sourceCurrencies, locale]);

    const targetCurrenciesOptions = useMemo(() => {
        if (!targetCurrencies || targetCurrencies.length === 0) return [];
        return Object.entries(targetCurrencies).map(([key, providers]) => ({
            value: key,
            providers,
            label: I18n.t(`${key}_DESCRIPTION`)
        }));
    }, [targetCurrencies, locale]);

    useEffect(() => {
        if (business) {
            if (!currencyCode) {
                setCurrencyCode(business?.current?.currencyCode);
            }

            statService.current
                .paymentMethodStat({
                    businessIdentifier: business?.current?.identifier,
                    isLive: business?.current?.live,
                    currencyCode: currencyCode,
                    balanceMode: balanceMode
                })
                .then((response) => {
                    let result = response.data.content
                    setTotalPrice(result.total)
                    const translatedLabels = result.labels.map(label => I18n.t(`${label}_PROVIDER_DESCRIPTION`));
                    const doughnutData = getDoughnutData(translatedLabels, result.data)
                    setDoughnutData(doughnutData)
                })
                .catch((err) => {
                    console.error('Error fetching paymentMethodStat:', err);
                });

            statService.current
                .paymentByCurrencyStat({
                    businessIdentifier: business?.current?.identifier,
                    isLive: business?.current?.live,
                    currencyCode: currencyCode,
                    balanceMode: balanceMode
                })
                .then((response) => {
                    let result = response.data.content
                    console.log(result)
                    setPrices(result)
                })
                .catch((err) => {
                    console.error('Error fetching paymentMethodStat:', err);
                });
        }
    }, [balanceMode, business, currencyCode]);


    const materialColors = [
        '#F44336', // red
        '#4CAF50', // green
        '#2196F3', // blue
        '#FFEB3B', // yellow
        '#9C27B0', // purple
        '#FF9800', // orange
        '#00BCD4', // cyan
        '#CDDC39', // lime
        '#E91E63', // pink
        '#009688', // teal
        '#FFC107', // amber
        '#3F51B5', // indigo
        '#8BC34A', // light green
        '#FF5722', // deep orange
        '#673AB7', // deep purple
        '#795548', // brown
        '#607D8B', // blue gray
        '#03A9F4', // light blue
    ];


    const shuffleArray = (array) => {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = i % Math.floor(arr.length / 2);
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    const getMaterialColors = (count) => {
        const base = shuffleArray(materialColors);
        const result = [];
        for (let i = 0; i < count; i++) {
            result.push(base[i % base.length]);
        }
        return result;
    };

    const getDoughnutData = (labels, data) => {
        const backgroundColor = getMaterialColors(labels.length);
        const borderColor = getComputedStyle(document.body).getPropertyValue('--divider-color') || 'rgba(160, 167, 181, .3)';

        return {
            labels: labels,
            datasets: [
                {
                    data: data,
                    backgroundColor,
                    borderColor
                }
            ]
        };
    };


    const getDoughnutOptions = () => {
        const textColor = getComputedStyle(document.body).getPropertyValue('--text-color') || 'rgba(0, 0, 0, 0.87)';
        const fontFamily = getComputedStyle(document.body).getPropertyValue('--font-family');

        return {
            plugins: {
                legend: {
                    position: 'right',
                    labels: {
                        fontFamily,
                        color: textColor,
                        padding: 20,
                        boxWidth: 12,
                        usePointStyle: true,
                        pointStyle: 'circle',
                        font: {
                            size: 12,
                            lineHeight: 1.2
                        },
                    }
                }
            },
            cutout: '50%',
            responsive: true,
            maintainAspectRatio: false
        };
    };


    const priceBodyTemplate = (row) => {
        return (
            <>
                <span className="p-column-title"> Total </span>
                {formatAmount(row.totalAmount, row.currencyCode, locale)}
            </>
        );
    };

    const currencyBodyTemplate = (row) => {
        return (
            <>
                <span className="p-column-title"> {I18n.t('CURRENCY')} </span>
                {row.currencyCode}
            </>
        );
    };

    const paymentMethodTemplate = (row) => {
        return (
            <>
                <span className="p-column-title"> {I18n.t('PAYMENT_METHOD')} </span>
                {I18n.t(`${row.paymentType}_PROVIDER_DESCRIPTION`)}
            </>
        );
    };

    const displayAmountFormatted = (fromAmount, fromCurrencyCode, fromProvider, toAmount, toCurrencyCode, toProvider) => {
        return formatAmount(fromAmount, fromCurrencyCode, locale) + " (" + I18n.t(`${fromProvider}_PROVIDER_DESCRIPTION`) + ')  →  ' +
            formatAmount(toAmount, toCurrencyCode, locale) + " (" + I18n.t(`${toProvider}_PROVIDER_DESCRIPTION`) + ")"
    };

    const typeBodyTemplate = useCallback((data) => (
        <>
            <span className="p-column-title">Type</span>
            {I18n.t(data.type)}
        </>
    ), []);

    const statusBodyTemplate = (data) => {
        let component;
        if ("PENDING" === data.status) {
            component = <Tag severity="info" value={I18n.t(data.status + '_DESCRIPTION')} outlined></Tag>
        } else if ("SUCCESS" === data.status) {
            component = <Tag severity="success" value={I18n.t(data.status + '_DESCRIPTION')}></Tag>
        } else if ("FAILED" === data.status) {
            component = <Tag severity="danger" value={I18n.t(data.status + '_DESCRIPTION')}></Tag>
        }

        return <>
            <span className="p-column-title">{I18n.t('STATUS')}</span>
            {component}
        </>
    };

    const dateBodyTemplate = useCallback((data) => (
        <>
            <span className="p-column-title">{I18n.t('OPERATION_DATE')}</span>
            {formatCustomDate(data.date, locale)}
        </>
    ), [locale]);

    const amountBodyTemplate = (data) => {
        let component;
        if ("CONVERSION" === data.type) {
            const fromProvider = data.fromProvider + (data.fromCountryCode === undefined ? "" : '_' + data.fromCountryCode)
            const toProvider = data.toProvider + (data.toCountryCode === undefined ? "" : '_' + data.toCountryCode)
            component = displayAmountFormatted(data.fromAmount, data.fromCurrencyCode, fromProvider, data.toAmount, data.toCurrencyCode, toProvider)
        } else if ("WITHDRAW" === data.type) {
            component = formatAmount(data.fromAmount, data.fromCurrencyCode, locale)
        }

        return <>
            <span className="p-column-title">{I18n.t('AMOUNT')}</span>
            {component}
        </>
    };


    const currencyDescriptionBodyTemplate = (row) => {
        return (
            <>
                <span className="p-column-title"> {I18n.t('CURRENCY_DESCRIPTION')} </span>
                {I18n.t(`${row.currencyCode}_DESCRIPTION`)}
            </>
        );
    };

    function leftToolbarTemplate() {
        return (<React.Fragment>
            <div className="table-header">
                <h5>{I18n.t('BALANCES')}</h5>
            </div>
        </React.Fragment>)
    }

    function rightToolbarTemplate() {
        return (<React.Fragment>
            <Button
                label={I18n.t('CONVERT_CURRENCY')}
                icon="pi pi-dollar"
                className="p-button-secondary p-mr-2"
                onClick={openConversionDialog}
            />
            <Button
                label={I18n.t('MAKE_WITHDRAWAL')}
                icon="pi pi-wallet"
                className="p-button-primary"
            />
            <Dropdown style={{marginLeft: '7px', padding: '0px'}}
                      value={balanceMode}
                      onChange={handleBalanceModeChange}
                      options={balanceModeOptions}
                      placeholder={I18n.t('SELECT_BALANCE_MODE')}/>
            <Dropdown style={{marginLeft: '7px', padding: '0px'}}
                      value={currencyCode}
                      onChange={handleCurrencyChange}
                      options={sourceCurrenciesOptions}
                      placeholder={I18n.t('SELECT_CURRENCY')}/>
        </React.Fragment>)
    }

    return (
        <div className="card">
            <Toast ref={toast}/>
            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}/>
            <TabView activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
                <TabPanel header={I18n.t('OVERVIEW')}>
                    <div className="p-grid dashboard">
                        <div className="p-col-12 p-md-4">
                            <div className="card widget-insights height-100">
                                <div className="card-header p-mb-2">
                                    <h5>{I18n.t('PAYMENT_METHOD_DISTRIBUTION')}</h5>
                                </div>
                                <div className="card-subheader p-m-4 p-d-flex p-ai-center">
                                    <h6>{I18n.t(balanceMode) + ': ' + formatAmount(totalPrice, currencyCode, locale)}</h6>
                                </div>
                                <div className="p-d-flex p-jc-center">
                                    <Chart ref={doughnut} type="pie" data={doughnutData}
                                           options={doughnutOptions}></Chart>
                                </div>
                            </div>
                        </div>

                        <div className="p-col-12 p-md-8">
                            <div className="card height-100">
                                <div className="card-header">
                                    <h5>{I18n.t('CURRENCY_METHOD_DISTRIBUTION')}</h5>
                                </div>

                                <DataTable value={prices} className="p-datatable-products"
                                           responsive="true" height="400px"
                                           selection={selectedProduct}
                                           onSelectionChange={(e) => setSelectedProduct(e.value)}>
                                    <Column body={priceBodyTemplate} header="Total"/>
                                    <Column body={paymentMethodTemplate} header={I18n.t('PAYMENT_METHOD')}/>
                                    <Column body={currencyBodyTemplate} header={I18n.t('CURRENCY')}/>
                                    <Column body={currencyDescriptionBodyTemplate}
                                            header={I18n.t('CURRENCY_DESCRIPTION')}/>
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header={I18n.t('MOVEMENTS_HISTORY')}>
                    <div className="card">
                        <DataTable
                            ref={dt}
                            value={elements}
                            lazy
                            emptyMessage={I18n.t('NO_ITEMS_FOUND')}
                            selectionMode="single"
                            dataKey="id"
                            paginator
                            loading={loading}
                            rows={pageSize}
                            totalRecords={totalRecords}
                            first={first}
                            onPage={onPage}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate={I18n.t('PAGE_REPORT_TEMPLATE')}
                            sortMode="single"
                            onSort={customSort}
                            sortField={sortFilterValue}
                            sortOrder={sortFilterOrder}
                            responsive="true"
                            className="p-datatable-products"
                            showGridlines={false}
                        >
                            <Column headerStyle={{width: '10rem'}} field="type" header="Type" sortable body={typeBodyTemplate}/>
                            <Column headerStyle={{width: '10rem'}} field="status" header={I18n.t('STATUS')} sortable body={statusBodyTemplate}/>
                            <Column field="date" header={I18n.t('OPERATION_DATE')} body={dateBodyTemplate}/>
                            <Column field="status" header={I18n.t('AMOUNT')} body={amountBodyTemplate}/>
                        </DataTable>
                    </div>
                </TabPanel>
            </TabView>

            <Dialog
                visible={showConversionDialog}
                style={{width: '500px'}}
                header={I18n.t('CONVERT_CURRENCY')}
                modal
                className="p-fluid"
                onHide={hideConversionDialog}
                footer={
                    <div>
                        <Button label={I18n.t('CANCEL')} icon="pi pi-times" className="p-button-text"
                                onClick={hideConversionDialog}/>
                    </div>
                }
            >
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={validationSchema}
                    onSubmit={estimateConvert}
                >
                    {({values, errors, touched, handleChange, handleBlur, setFieldValue, isValid}) => (
                        <Form className="p-fluid p-mt-1">

                            <div className="p-grid">
                                <div className="p-col-6">
                                    <div className="p-field">
                                         <span className="p-float-label">
                                            <Field name="fromCurrencyCode">
                                                {({field}) => (
                                                    <Dropdown
                                                        id="fromCurrencyCode"
                                                        value={field.value}
                                                        options={sourceCurrenciesOptions}
                                                        onChange={(e) => {
                                                            setFieldValue('fromCurrencyCode', e.value);
                                                            setToAmount(null);
                                                        }}
                                                        className={errors.fromCurrencyCode && touched.fromCurrencyCode ? 'p-invalid' : ''}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="fromCurrencyCode" component="small"
                                                          className="p-error"/>
                                            <label htmlFor="fromCurrencyCode">{I18n.t('SOURCE_CURRENCY')}</label>
                                         </span>
                                    </div>
                                </div>
                                <div className="p-col-6">
                                    <div className="p-field">
                                        <span className="p-float-label">
                                            <Field name="fromProvider">
                                                {({field, form}) => {
                                                    const providerList = sourceCurrenciesOptions
                                                        .find(opt => opt.value === form.values.fromCurrencyCode)
                                                        ?.providers || [];

                                                    const providerOptions = providerList.map(provider => ({
                                                        value: provider,
                                                        label: I18n.t(`${provider}_PROVIDER_DESCRIPTION`)
                                                    }));

                                                    return (
                                                        <Dropdown
                                                            id="fromProvider"
                                                            value={field.value}
                                                            options={providerOptions}
                                                            onChange={(e) => {
                                                                setFieldValue('fromProvider', e.value);
                                                                setToAmount(null);
                                                            }}
                                                            className={errors.fromProvider && touched.fromProvider ? 'p-invalid' : ''}
                                                        />);
                                                }}
                                            </Field>
                                            <ErrorMessage name="fromProvider" component="small" className="p-error"/>
                                            <label htmlFor="fromProvider">{I18n.t('SOURCE_PROVIDER')}</label>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-grid">
                                <div className="p-col-6">
                                    <div className="p-field">
                                         <span className="p-float-label">
                                            <Field name="toCurrencyCode">
                                                {({field}) => (
                                                    <Dropdown
                                                        id="toCurrencyCode"
                                                        value={field.value}
                                                        options={targetCurrenciesOptions}
                                                        onChange={(e) => {
                                                            console.log(e)
                                                            setFieldValue('toCurrencyCode', e.value);
                                                            setToAmount(null);
                                                        }}
                                                        className={errors.toCurrencyCode && touched.toCurrencyCode ? 'p-invalid' : ''}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage name="toCurrencyCode" component="small" className="p-error"/>
                                            <label htmlFor="toCurrencyCode">{I18n.t('TARGET_CURRENCY')}</label>
                                         </span>
                                    </div>
                                </div>
                                <div className="p-col-6">
                                    <div className="p-field">
                                        <span className="p-float-label">
                                            <Field name="toProvider">
                                                {({field, form}) => {
                                                    const providerList = targetCurrenciesOptions
                                                        .find(opt => opt.value === form.values.toCurrencyCode)
                                                        ?.providers || [];

                                                    const providerOptions = providerList.map(provider => ({
                                                        value: provider,
                                                        label: I18n.t(`${provider}_PROVIDER_DESCRIPTION`)
                                                    }));

                                                    return (
                                                        <Dropdown
                                                            id="toProvider"
                                                            value={field.value}
                                                            options={providerOptions}
                                                            onChange={(e) => {
                                                                setFieldValue('toProvider', e.value);
                                                                setToAmount(null);
                                                            }}
                                                            className={errors.toProvider && touched.toProvider ? 'p-invalid' : ''}
                                                        />);
                                                }}
                                            </Field>
                                            <ErrorMessage name="toProvider" component="small" className="p-error"/>
                                            <label htmlFor="toProvider">{I18n.t('TARGET_PROVIDER')}</label>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-grid">
                                <div className="p-col-6">
                                    <div className="p-field">
                                <span className="p-float-label">
                                    <Field name="fromAmount">
                                        {({field}) => (
                                            <InputNumber
                                                id="fromAmount"
                                                value={field.value}
                                                onValueChange={(e) => {
                                                    setFieldValue('fromAmount', e.value);
                                                    setToAmount(null);
                                                }}
                                                mode="decimal"
                                                minFractionDigits={2}
                                                maxFractionDigits={2}
                                                className={errors.fromAmount && touched.fromAmount ? 'p-invalid' : ''}
                                            />
                                        )}
                                    </Field>
                                    <label htmlFor="fromAmount">{I18n.t('AMOUNT')}</label>
                                    <ErrorMessage name="fromAmount" component="small" className="p-error"/>
                                </span>
                                    </div>
                                </div>
                                <div className="p-col-6">
                                    <Button
                                        type="submit"
                                        label={I18n.t('PREVIEW')}
                                        icon="pi pi-eye"
                                        className="p-button-secondary"
                                        disabled={toAmount != null}
                                        loading={isLoading}
                                    />
                                </div>
                            </div>

                            {toAmount != null &&
                                <div className="p-px-2 p-py-2 p-shadow-2 text-center">
                                    <h6 style={{lineHeight: '1.5'}}>{displayAmountFormatted(values.fromAmount, values.fromCurrencyCode, values.fromProvider, toAmount, values.toCurrencyCode, values.toProvider)}</h6>
                                </div>}
                        </Form>
                    )}
                </Formik>
                {toAmount != null &&
                    <div className="p-d-flex p-jc-end p-mt-3">
                        <Button
                            label={I18n.t('CONVERT')}
                            icon="pi pi-check"
                            className="p-button-primary"
                            onClick={handleConvert}
                            loading={isLoading}
                        />
                    </div>}
            </Dialog>
        </div>
    )
}
